<template>
    <div class="pt-2" style="height: calc(100vh - 220px)">
        <div class="flex items-start pt-4 relative h-full overflow-hidden">
            <button
                v-show="!courseSidebarOpen"
                class="btn-icon btn-white absolute top-4 right-1 z-10"
                @click="store.setCourseSidebarOpen()"
                id="hs-course-show-sidebar"
            >
                <Icon icon="list" />
            </button>

            <div
                class="flex-1 transition-all h-full pr-6 lg:pr-0 overflow-y-auto flex flex-col justify-start pb-16"
                ref="contentContainer"
                @scroll="handleScroll"
            >
                <LoadingState
                    :loading="loadingCourse"
                    class="absolute top-5 left-1/2 -translate-x-1/2 z-20"
                />

                <div class="flex-1 max-w-2xl mx-auto">
                    <div ref="reference" class="relative">
                        <h3
                            class="text-slate-800 text-2xl lg:text-3xl xl:text-4xl font-bold mb-8"
                        >
                            {{ chapter?.title }}
                        </h3>
                        <div
                            class="prose prose-courses"
                            v-html="chapter?.description"
                        ></div>
                    </div>
                </div>
                <div
                    v-show="!scrolledToBottom"
                    class="w-full h-28 absolute bottom-0 left-0 bg-gradient-to-b from-slate-100/0 via-slate-100/70 via-50% to-slate-100 z-20"
                ></div>
            </div>
            <div
                class="absolute pb-1 w-max flex items-center justify-center lg:justify-end gap-4 transition-transform z-20"
                v-if="chapter"
                ref="buttonContainer"
            >
                <button
                    class="btn-base btn-white"
                    @click="gotoPreviousChapter()"
                    v-if="previousChapter"
                    id="hs-course-previous-chapter"
                >
                    <Icon icon="arrow-right" class="rotate-180" />
                    Previous
                </button>
                <button
                    class="btn-base btn-10x-orange"
                    @click="completeChapter()"
                    v-if="!chapter?.completed && nextChapter"
                    id="hs-course-complete-chapter"
                >
                    Complete and continue
                    <Icon icon="arrow-right" />
                </button>
                <button
                    class="btn-base btn-10x-orange"
                    @click="completeCourse()"
                    v-if="!chapter?.completed && currentChapter && !nextChapter"
                    id="hs-course-complete-course"
                >
                    Complete Course
                    <Icon icon="arrow-right" />
                </button>
            </div>
            <!-- start navigation aside:  -->
            <aside
                class="transition-all h-full relative overflow-hidden"
                :class="{
                    'translate-x-full w-0': !courseSidebarOpen,
                    'translate-x-0 w-full lg:w-[360px]': courseSidebarOpen,
                }"
            >
                <LoadingState
                    :loading="loadingCourse"
                    class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20"
                />

                <button
                    class="btn-icon btn-transparent flex items-center justify-center absolute top-2 right-1 rounded-full bg-gray-100 w-9 h-9 focus:outline-none focus:border focus:border-slate-300 z-20"
                    @click="store.setCourseSidebarOpen()"
                    v-if="courseSidebarOpen"
                    id="hs-course-close-sidebar"
                >
                    <Icon icon="close" />
                </button>
                <CourseSidebar />
                <div
                    class="w-full h-20 absolute bottom-0 left-0 bg-gradient-to-b from-slate-100/10 to-slate-100 z-20"

                ></div>
            </aside>
        </div>
    </div>
</template>
<script setup>
import { useRouter, useRoute } from 'vue-router';
import { useCoursesStore } from '@/store/courses';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import { flip, computePosition } from '@floating-ui/vue';
import CourseSidebar from '@/components/courses/course-sidebar/course-sidebar.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { computed, onMounted, ref, watch, onUnmounted } from 'vue';

const store = useCoursesStore();
const router = useRouter();
const appStore = useAppStore();
const route = useRoute();

const { sidebarOpen } = storeToRefs(appStore);

const {
    course,
    chapter,
    chapters,
    loadingCourse,
    courseSidebarOpen,
    currentChapter,
} = storeToRefs(store);

const contentContainer = ref(null);
const reference = ref(null);
const buttonContainer = ref(null);
const scrolledToBottom = ref(false);

const updatePosition = () => {
    if (reference.value && buttonContainer.value) {
        const aside = document.querySelector('aside');
        let rightOffset = 0;

        if (courseSidebarOpen.value && aside) {
            const asideWidth = aside.offsetWidth;
            rightOffset = asideWidth;
        }

        computePosition(reference.value, buttonContainer.value, {
            placement: 'bottom-start',
            middleware: [flip()],
        }).then(({ x, y }) => {
            Object.assign(buttonContainer.value.style, {
                right: `${x + rightOffset}px`,
                bottom: `0px`,
            });
        });
    }
};

const handleScroll = () => {
    if (!contentContainer.value) return;
    const { scrollTop, scrollHeight, clientHeight } = contentContainer.value;

    scrolledToBottom.value = scrollTop + clientHeight >= scrollHeight - 1;
};

const gotoPreviousChapter = () => {
    router.push({
        name: 'Chapter',
        params: {
            slug: course.value.slug,
            chapterSlug: previousChapter.value.slug,
        },
    });
};

const nextChapter = computed(() => {
    const chapterIndex = chapters.value.findIndex(
        (chpt) => chpt?.uuid === chapter?.value?.uuid
    );

    if (chapters.value[chapterIndex + 1]) {
        return chapters.value[chapterIndex + 1];
    }

    return null;
});

const previousChapter = computed(() => {
    const chapterIndex = chapters.value.findIndex(
        (chpt) => chpt?.uuid === chapter?.value?.uuid
    );
    return chapters.value[chapterIndex - 1];
});

const completeChapter = () => {
    store.completeChapter(chapter.value.uuid);
    router.push({
        name: 'Chapter',
        params: {
            //slug: course.value.slug,
            chapterSlug: nextChapter.value.slug,
        },
    });
};

const completeCourse = () => {
    store.completeChapter(chapter.value.uuid);
    router.push({
        name: 'Course',
        params: {
            slug: course.value.slug,
        },
    });
};

watch([() => courseSidebarOpen.value, () => sidebarOpen.value], () => {
    setTimeout(() => {
        updatePosition();
    }, 300);
});

onMounted(() => {
    window.addEventListener('resize', updatePosition);

    updatePosition();

    if (route.params.chapterSlug) {
        store
            .getChapter(route.params.chapterSlug, route.params.slug)
            .then(() => {
                if (chapter.value.user_status === 'new') {
                    store.startChapter(chapter.value.uuid);
                }
                //appStore.setBreadcrumbs('slug', course.value.title);
                appStore.setBreadcrumbs('chapterSlug', chapter.value.title);
                store.setCurrentChapter(chapter.value);
                updatePosition();
                handleScroll();
            });
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', updatePosition);
});
</script>
